import { take, call, select, fork, race, cancel } from 'redux-saga/effects';
import moment from 'moment-timezone';
import * as _ from 'lodash';

import {
  fetchReports,
  fetchReportsByDate,
  fetchLeftCostReports,
  fetchRightCostReports,
  fetchLastJourney,
} from './actions';
import { login } from '../auth/actions';
import { fetchDevices } from '../devices/actions';
import { bindAsyncAction } from '../../utils/sagas';
import { changeCurrentDevice } from '../../components/Header/actions';
import { logoutButtonClick } from '../../components/Auth/actions';
import {
  changeCurrentRange,
  filterByMonth,
} from '../../components/Dashboard/actions';
import { apiRequest, geoCodeRequest } from '../../utils/api';

const worker = bindAsyncAction(fetchReports, function* load(params) {
  return yield call(apiRequest, params);
});

const byDateWorker = bindAsyncAction(fetchReportsByDate, function* load(
  params,
) {
  return yield call(apiRequest, params);
});

const leftCostReportWorker = bindAsyncAction(
  fetchLeftCostReports,
  function* load(params) {
    return yield call(apiRequest, params);
  },
);

const rightCostReportWorker = bindAsyncAction(
  fetchRightCostReports,
  function* load(params) {
    return yield call(apiRequest, params);
  },
);

const lastJourneyWorker = bindAsyncAction(fetchLastJourney, function* load(
  params,
) {
  const { data } = yield call(apiRequest, params);
  const journey = data[0];
  try {
    const geoResponse = yield call(geoCodeRequest, {
      body: JSON.stringify({
        requests: [
          {
            Latitude: journey.journey.stat.startLat / 10000000,
            Longitude: journey.journey.stat.startLon / 10000000,
          },
          {
            Latitude: journey.journey.stat.finishLat / 10000000,
            Longitude: journey.journey.stat.finishLon / 10000000,
          },
        ],
      }),
    });

    journey.geoData = {
      start: geoResponse[0],
      finish: geoResponse[1],
    };

    const events = journey.journey.events.filter(e => e.penalties);

    if (events.length) {
      try {
        const eventsGeoResponse = yield call(geoCodeRequest, {
          body: JSON.stringify({
            requests: events.map(e => ({
              Latitude: e.lat / 10000000,
              Longitude: e.lon / 10000000,
            })),
          }),
        });

        const eventsWithGeoData = events.map((e, index) => {
          e.geoData = eventsGeoResponse[index];
          return e;
        });
        journey.journey.events = journey.journey.events.map(e =>
          _.extend(e, _.find(eventsWithGeoData, item => item.id === e.id)),
        );
      } catch (e) {
        return;
      }
    }
  } catch (e) {
    return;
  }

  return journey; // eslint-disable-line consistent-return
});

function* getReportsSaga() {
  const deviceId = yield select(state => state.devices.currentDevice.id);
  try {
    yield call(worker, {
      url: '/api/v2/Reports',
      params: {
        Devices: [deviceId],
        Types: [
          'Distance',
          'Duration',
          'FuelCost',
          'FuelConsumption',
          'Speeding',
          'Cornering',
          'Braking',
          'Accelerating',
          'Scoring',
        ],
        Unit: 'Day',
        UtcOffsetInMinutes: new Date().getTimezoneOffset(),
        From: moment()
          .subtract(29, 'days')
          .startOf('day')
          .format('YYYY-MM-DD'),
        UpTo: moment()
          .endOf('day')
          .format('YYYY-MM-DD'),
      },
    });
  } catch (e) {
    throw new Error(e);
  }

  while (true) {
    const { payload } = yield take(changeCurrentRange.type);

    try {
      yield call(byDateWorker, {
        url: '/api/v2/Reports',
        params: {
          Devices: [deviceId],
          Unit: 'Day',
          Types: [
            'Distance',
            'Duration',
            'FuelCost',
            'FuelConsumption',
            'Speeding',
            'Cornering',
            'Braking',
            'Accelerating',
          ],
          UtcOffsetInMinutes: new Date().getTimezoneOffset(),
          From: payload.From.startOf('day').format('YYYY-MM-DD'),
          UpTo: payload.UpTo.endOf('day').format('YYYY-MM-DD'),
        },
      });
    } catch (e) {
      throw new Error(e);
    }
  }
}

function* filterByMonthSaga() {
  const deviceId = yield select(state => state.devices.currentDevice.id);
  try {
    yield fork(leftCostReportWorker, {
      url: '/api/v2/Reports',
      params: {
        Devices: [deviceId],
        Unit: 'Day',
        Types: ['FuelCost'],
        UtcOffsetInMinutes: new Date().getTimezoneOffset(),
        From: moment()
          .utc()
          .subtract(1, 'months')
          .startOf('month')
          .format(),
        UpTo: moment()
          .utc()
          .subtract(1, 'months')
          .endOf('month')
          .format(),
      },
    });
  } catch (e) {
    throw new Error(e);
  }
  try {
    yield fork(rightCostReportWorker, {
      url: '/api/v2/Reports',
      params: {
        Devices: [deviceId],
        Unit: 'Day',
        Types: ['FuelCost'],
        UtcOffsetInMinutes: new Date().getTimezoneOffset(),
        From: moment()
          .utc()
          .startOf('month')
          .format(),
        UpTo: moment()
          .utc()
          .endOf('month')
          .format(),
      },
    });
  } catch (e) {
    throw new Error(e);
  }

  while (true) {
    const filter = yield take(filterByMonth.type);
    const w = filter.payload.isLeft
      ? leftCostReportWorker
      : rightCostReportWorker;

    try {
      yield fork(w, {
        url: '/api/v2/Reports',
        params: {
          Devices: [deviceId],
          Unit: 'Day',
          Types: ['FuelCost'],
          UtcOffsetInMinutes: new Date().getTimezoneOffset(),
          From: filter.payload.date.startOf('month').format(),
          UpTo: filter.payload.date.endOf('month').format(),
        },
      });
    } catch (e) {
      throw new Error(e);
    }
  }
}

function* lastJourneySaga() {
  const deviceId = yield select(state => state.devices.currentDevice.id);
  try {
    yield call(lastJourneyWorker, {
      url: '/api/v3/Journeys',
      params: {
        Devices: [deviceId],
        Take: 1,
        Skip: 0,
        Sort: '-starttime',
      },
    });
  } catch (e) {
    return;
  }

  while (true) {
    const { payload } = yield take(changeCurrentDevice.type);

    try {
      yield call(lastJourneyWorker, {
        url: '/api/v3/Journeys',
        params: {
          Devices: [payload.device.id],
          Take: 1,
          Skip: 0,
          Sort: '-starttime',
        },
      });
    } catch (e) {
      return;
    }
  }
}

function* getFirstReportsSaga() {
  const devices = yield select(state => state.devices.devices);
  if (devices.length) {
    while (true) {
      const url = yield select(
        state => state.routing.locationBeforeTransitions.pathname,
      );
      if (url === '/') {
        yield fork(getReportsSaga);
        yield fork(filterByMonthSaga);
        yield fork(lastJourneySaga);
      }

      yield race({
        changeDevice: take(changeCurrentDevice.type),
        changeLocation: take('@@router/LOCATION_CHANGE'),
      });
    }
  }
}

export default function* dashboardSaga() {
  while (true) {
    yield take(login.done.type);
    yield take(fetchDevices.done.type);

    const firstReportsSaga = yield fork(getFirstReportsSaga);
    yield take(logoutButtonClick.type);
    yield cancel(firstReportsSaga);
  }
}



// WEBPACK FOOTER //
// ./src/sagas/dashboard/index.js